import { config } from '@/config'
import { getToken, invalidateToken, isTokenExpired } from '@/auth/session'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly url: string,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isAuth(): boolean {
    return this.status === 401 || this.status === 403
  }
}

export type QueryValue = string | number | boolean | undefined | null | string[]

function buildUrl(path: string, params?: Record<string, QueryValue>, apiBase?: string): string {
  // La base puede ser absoluta (`https://staging.cotalker.com`) o relativa
  // (`/cotalker-api`, cuando en desarrollo se pasa por el proxy de Vite para
  // esquivar la lista blanca de CORS). El segundo argumento resuelve la
  // relativa contra el origen propio y no molesta cuando ya es absoluta.
  const base = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
  const url = new URL((apiBase ?? config.COTALKER_API_URL) + path, base)
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      // La API espera ids repetidos como ids[]=a&ids[]=b
      for (const v of value) url.searchParams.append(`${key}[]`, v)
    } else {
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, QueryValue>,
  signal?: AbortSignal,
  apiBase?: string,
): Promise<T> {
  return request<T>('GET', buildUrl(path, params, apiBase), undefined, signal)
}

async function request<T>(
  method: 'GET' | 'POST',
  url: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  // El token ya venció: se pide uno fresco y se corta acá en vez de gastar una
  // llamada que se sabe que vuelve 401. Los observados duran 1 hora.
  if (isTokenExpired()) {
    invalidateToken()
    throw new ApiError('La sesión expiró.', 401, url)
  }

  const token = getToken()

  let res: Response
  try {
    res = await fetch(url, {
      method,
      signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
  } catch (cause) {
    if ((cause as Error)?.name === 'AbortError') throw cause
    throw new ApiError('No se pudo conectar con Cotalker.', 0, url, cause)
  }

  if (res.status === 401) {
    // El contenedor renueva; el portal solo avisa y pide uno fresco.
    invalidateToken()
    throw new ApiError('La sesión expiró.', 401, url)
  }

  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = undefined
    }
    throw new ApiError(`Cotalker respondió ${res.status}.`, res.status, url, body)
  }

  try {
    return (await res.json()) as T
  } catch (cause) {
    throw new ApiError('Cotalker devolvió una respuesta ilegible.', res.status, url, cause)
  }
}

/**
 * Las respuestas de Cotalker no tienen una sola forma: v1 suele devolver el
 * recurso pelado y v2 lo envuelve en `data`, a veces con una clave más adentro
 * (`data.tasks`, `data.properties`). En vez de asumir una, se prueban las
 * conocidas — asumir mal acá devuelve `undefined` sin lanzar error, que es
 * exactamente el modo de falla silenciosa que este portal tiene que evitar.
 */
export function pickArray<T>(payload: unknown, keys: string[]): T[] {
  if (Array.isArray(payload)) return payload as T[]
  if (!payload || typeof payload !== 'object') return []

  const root = payload as Record<string, unknown>
  const candidates: unknown[] = [root, root['data']]

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as T[]
    if (!candidate || typeof candidate !== 'object') continue
    const obj = candidate as Record<string, unknown>
    for (const key of keys) {
      if (Array.isArray(obj[key])) return obj[key] as T[]
    }
  }
  return []
}

/** Igual que `pickArray`, para un recurso único. */
export function pickObject<T>(payload: unknown, keys: string[] = []): T | null {
  if (!payload || typeof payload !== 'object') return null
  const root = payload as Record<string, unknown>

  for (const key of keys) {
    const v = root[key]
    if (v && typeof v === 'object') return v as T
  }
  const data = root['data']
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>
    for (const key of keys) {
      const v = inner[key]
      if (v && typeof v === 'object') return v as T
    }
    if (!Array.isArray(data)) return data as T
  }
  if (typeof root['_id'] === 'string') return root as T
  return null
}
